import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Table, Pagination, Row, Input, Button, Tabs } from "antd";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import Endpoints from "../../../api/endpoints.js";
import useSuccessErrorModal from "../../../hooks/successErrorModal/useSuccessErrorModal";
import routes from "../../../utils/routes.js";
import EndpointFunctions from "../../../utils/Functions/endpointFunctions.js";
import { UserNotificationColumns } from "./UserNotificationColumns.js";

const UserNotifications = () => {
  const { showModal, closeModal, ModalComponent } = useSuccessErrorModal();

  // Table state
  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  // For search input values (controlled components)
  const [userId, setUserId] = useState("");
  const [notificationId, setNotificationId] = useState("");

  // “Committed” search values used for fetching.
  // These are updated only when the user clicks Search.
  const [committedUserId, setCommittedUserId] = useState("");
  const [committedNotificationId, setCommittedNotificationId] = useState("");

  // Pagination and sorting state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    sortField: "id",
    sortDirection: "desc",
    activeKey: Endpoints.getAllUserNotifications,
  });

  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();

  // Fetch data when any pagination/sort parameter changes.
  // For search tabs, only fetch if a search has been committed.
  useEffect(() => {
    if (
      pagination.activeKey === Endpoints.getAllUserNotificationsByUserId &&
      !committedUserId
    ) {
      return;
    }
    if (
      pagination.activeKey ===
        Endpoints.getAllUserNotificationsByNotificationId &&
      !committedNotificationId
    ) {
      return;
    }
    fetchUserNotificationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.current,
    pagination.pageSize,
    pagination.sortField,
    pagination.sortDirection,
    pagination.activeKey,
    committedUserId,
    committedNotificationId,
  ]);

  const fetchUserNotificationData = async () => {
    setTableLoading(true);

    let httpParams = {
      page: pagination.current - 1,
      size: pagination.pageSize,
      sortField: pagination.sortField,
      sortDirection: pagination.sortDirection,
    };

    if (
      pagination.activeKey === Endpoints.getAllUserNotificationsByNotificationId
    ) {
      httpParams.notificationId = committedNotificationId;
    } else if (
      pagination.activeKey === Endpoints.getAllUserNotificationsByUserId
    ) {
      httpParams.userId = committedUserId;
    }

    const result = await EndpointFunctions.fetchData({
      endpointURL: pagination.activeKey,
      axiosPrivate: axiosPrivate,
      httpParams: httpParams,
    });

    if (result.success) {
      const paginationResult = result.data;
      const formattedData = result.data.data;
      setTableData(formattedData);
      setPagination((prev) => ({
        ...prev,
        total: paginationResult.total,
        current: paginationResult.pageNumber + 1,
      }));
    } else {
      showModal(result.errorTitle, result.errorMessage, () => {
        closeModal();
      });
    }
    setTableLoading(false);
  };

  // When the page or pageSize changes
  const paginationChange = (page, pageSize) => {
    setPagination((prev) => ({ ...prev, current: page, pageSize }));
  };

  // When sorting changes
  const handleTableChange = (pagination, filters, sorter, extra) => {
    setPagination((prev) => ({
      ...prev,
      sortField: sorter.columnKey,
      sortDirection: sorter.order === "ascend" ? "asc" : "desc",
    }));
  };

  // Navigation for details page
  const goToDetailsPage = ({ record }) => {
    const id = record.id;
    let path = `${routes.userNotificationDetails}/${id}`;
    navigate(path);
  };

  // Input change handlers
  const handleUserIdChange = (e) => {
    setUserId(e.target.value);
  };

  const handleNotificationIdChange = (e) => {
    setNotificationId(e.target.value);
  };

  // When the user clicks "Search" on the User ID tab:
  // • Reset pagination to page 1
  // • Commit the userId for fetching.
  const handleSearchByUserId = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    setCommittedUserId(userId);
  };

  // When the user clicks "Search" on the Notification ID tab:
  // • Reset pagination to page 1
  // • Commit the notificationId for fetching.
  const handleSearchByNotificationId = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    setCommittedNotificationId(notificationId);
  };

  // When switching tabs, reset table data, pagination, and (if applicable) clear search values.
  const handleTabChange = (activeKey) => {
    setTableData([]);
    setPagination((prev) => ({
      ...prev,
      current: 1,
      activeKey: activeKey,
    }));

    if (activeKey === Endpoints.getAllUserNotificationsByUserId) {
      setCommittedUserId("");
      setUserId("");
    } else if (
      activeKey === Endpoints.getAllUserNotificationsByNotificationId
    ) {
      setCommittedNotificationId("");
      setNotificationId("");
    }
  };

  //tabs
  const items = [
    {
      key: Endpoints.getAllUserNotifications,
      label: "Tüm Bildirimler",
      children: (
        <Table
          dataSource={tableData}
          columns={UserNotificationColumns(goToDetailsPage)}
          scroll={{ x: true }}
          loading={tableLoading}
          pagination={false}
          onChange={handleTableChange}
          tableLayout="fixed"
        />
      ),
    },
    {
      key: Endpoints.getAllUserNotificationsByUserId,
      label: "Kullanıcıya Özel Bildirimler",
      children: (
        <>
          <Row justify="center" align="middle" style={{ marginBottom: 20 }}>
            <Input
              placeholder="Kullanıcı Id"
              style={{ width: 200, marginRight: 10 }}
              value={userId}
              onChange={handleUserIdChange}
            />
            <Button type="primary" onClick={handleSearchByUserId}>
              Filtrele
            </Button>
          </Row>
          <Table
            dataSource={tableData}
            columns={UserNotificationColumns(goToDetailsPage)}
            scroll={{ x: true }}
            loading={tableLoading}
            pagination={false}
            onChange={handleTableChange}
            tableLayout="fixed"
          />
        </>
      ),
    },
    {
      key: Endpoints.getAllUserNotificationsByNotificationId,
      label: "Bildirim Id'sine Göre Bildirimler",
      children: (
        <>
          <Row justify="center" align="middle" style={{ marginBottom: 20 }}>
            <Input
              placeholder="Bildirim Id"
              style={{ width: 200, marginRight: 10 }}
              value={notificationId}
              onChange={handleNotificationIdChange}
            />
            <Button type="primary" onClick={handleSearchByNotificationId}>
              Filtrele
            </Button>
          </Row>
          <Table
            dataSource={tableData}
            columns={UserNotificationColumns(goToDetailsPage)}
            scroll={{ x: true }}
            loading={tableLoading}
            pagination={false}
            onChange={handleTableChange}
            tableLayout="fixed"
          />
        </>
      ),
    },
  ];

  return (
    <>
      <Tabs
        activeKey={pagination.activeKey}
        onChange={handleTabChange}
        items={items}
        type="card"
      />
      <ModalComponent />
      <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
        <Pagination
          current={pagination.current}
          onChange={paginationChange}
          showSizeChanger
          total={pagination.total}
          showTotal={(total, range) =>
            `${range[0]}-${range[1]} / ${total} kayıt`
          }
          pageSize={pagination.pageSize}
        />
      </div>
    </>
  );
};

export default UserNotifications;
